import React from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {StyledGeneralDescriptionSpan} from 'app/activity/shared-utils';
import {MultipleUsersAvatar} from 'app/components/general/avatars';
import ElapsedTime from 'app/components/general/ElapsedTime';
import {getNotificationDescription, VERB_ICONS} from 'app/notifly/utils';
import {gt} from 'app/utils/i18n';
import {reportToSentry} from 'app/utils/sentry';

import type {NotificationType} from 'app/notifly/types';

interface Props {
  object: NotificationType;
  currentUserURL: string;
  markNotificationRead: (notification: NotificationType) => void;
}

type State = {
  hasError: boolean;
};

export class NotificationItem extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  componentDidCatch(error: any) {
    if (!this.state.hasError) {
      this.setState({
        hasError: true,
      });
      reportToSentry(error, {}, true);
      this.props.markNotificationRead(this.props.object);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <li className="row py-3 d-flex align-items-start align-items-sm-center">
          <div className="col-auto">
            <i className="fa fa-exclamation-triangle" />{' '}
            {gt('Unable to display notification')}
          </div>
        </li>
      );
    }

    const notification = this.props.object;
    const {actors, timestamp, verb, read_at} = notification;
    const currentUserURL = this.props.currentUserURL;

    return (
      <StyledContainerLI read={read_at != null}>
        <div className="col-auto d-none d-sm-block order-1">
          <i className={`fa-fw ${VERB_ICONS[verb]}`} />
        </div>
        <div className="col-2 col-sm-auto order-2 d-flex pr-0 pr-sm-1 flex-column flex-sm-row align-items-center">
          <MultipleUsersAvatar users={actors} />
          <div className="unread-badge-container flex-shrink-1 d-flex justify-content-center justify-content-sm-end  align-items-center">
            {read_at == null ? <div className="unread-badge" /> : null}
          </div>
        </div>
        <div className="col order-3">
          <NotificationDescription
            notification={notification}
            currentUserURL={currentUserURL}
          />
        </div>
        <div className="col-10 col-sm-2 offset-2 offset-sm-0 order-6 order-sm-4">
          <ElapsedTime
            tooltipPlacement="top"
            className="text-secondary font-italic py-1"
            timestamp={timestamp}
          />
        </div>
        <div
          className={cs(
            'col-2 order-5 justify-content-end',
            read_at != null ? 'd-none d-sm-flex' : 'd-flex'
          )}
        >
          {read_at != null ? (
            <ElapsedTime
              tooltipPlacement="top"
              className="text-secondary font-italic py-1 small"
              timestamp={read_at}
              wrapText={'Read %s'}
            />
          ) : (
            <button
              className="btn btn-link py-1"
              onClick={() => this.props.markNotificationRead(notification)}
              title={gt('Mark as read')}
            >
              <i className="fa fa-check" />
            </button>
          )}
        </div>
      </StyledContainerLI>
    );
  }
}

const StyledContainerLI = styled.li.attrs<{read: boolean}>({
  className:
    'row py-3 d-flex align-items-start align-items-sm-center border-bottom',
})<{read: boolean}>`
  background: ${({read}) => (read ? '#F3F3F3' : 'transparent')};
  opacity: ${({read}) => (read ? '0.7' : '1')};

  .unread-badge-container {
    width: 24px;
    height: 24px;

    .unread-badge {
      width: 8px;
      height: 8px;
      background: ${colors.lightLinkBlue};
      background: radial-gradient(
        ellipse at center,
        ${colors.lightLinkBlue} 0%,
        ${colors.linkBlue} 100%
      );
      border-radius: 50%;
    }
  }
`;

const StyledDescriptionSpan = styled(StyledGeneralDescriptionSpan)`
  .broadcast-message-signature {
    font-size: 80%;
    font-style: italic;
  }

  .target-ct {
    font-size: 80%;
  }

  .broadcast-message-content p {
    margin-bottom: 0.2rem;
  }
`;

const NotificationDescription = ({
  notification,
}: {
  notification: NotificationType;
  currentUserURL: string;
}) => (
  <StyledDescriptionSpan>
    {getNotificationDescription(notification)}
  </StyledDescriptionSpan>
);
